import type { Area } from 'react-easy-crop'

export type CropExportOptions = {
  /** MIME for the exported file. Prefer image/png for logos with transparency. */
  mimeType?: string
  /** JPEG/WebP quality 0–1. */
  quality?: number
  /** Longest edge cap in pixels (width or height). */
  maxEdge?: number
  fileName?: string
  /** Remove transparent / near-white margins around the artwork (logos). Output may be non-square. */
  trimMargins?: boolean
}

/** Bounding box of non-transparent, non-near-white pixels, or null when the canvas is blank. */
function findContentBounds(canvas: HTMLCanvasElement): { x: number; y: number; w: number; h: number } | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  const { width, height } = canvas
  const { data } = ctx.getImageData(0, 0, width, height)
  // If the canvas has transparency, white pixels are artwork (e.g. a white logo); otherwise white is background.
  let hasTransparency = false
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] <= 16) {
      hasTransparency = true
      break
    }
  }
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const nearWhite = !hasTransparency && data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245
      if (data[i + 3] > 16 && !nearWhite) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }
  if (maxX < 0) return null
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 }
}

/** Returns a tightly cropped copy (with a small even margin), or the original if nothing to trim. */
function trimCanvas(source: HTMLCanvasElement, opaqueBackground: boolean): HTMLCanvasElement {
  const b = findContentBounds(source)
  if (!b) return source
  if (b.w >= source.width * 0.97 && b.h >= source.height * 0.97) return source
  const pad = Math.round(Math.max(b.w, b.h) * 0.03)
  const sx = Math.max(0, b.x - pad)
  const sy = Math.max(0, b.y - pad)
  const sw = Math.min(source.width - sx, b.w + pad * 2)
  const sh = Math.min(source.height - sy, b.h + pad * 2)
  const out = document.createElement('canvas')
  out.width = sw
  out.height = sh
  const octx = out.getContext('2d')
  if (!octx) return source
  if (opaqueBackground) {
    octx.fillStyle = '#ffffff'
    octx.fillRect(0, 0, sw, sh)
  }
  octx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh)
  return out
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', () => reject(new Error('Could not load image for cropping')))
    img.crossOrigin = 'anonymous'
    img.src = src
  })
}

/**
 * Crop `imageSrc` to `pixelCrop` and return a File suitable for `POST /uploads/images`.
 */
export async function getCroppedImageFile(
  imageSrc: string,
  pixelCrop: Area,
  options: CropExportOptions = {},
): Promise<File> {
  const {
    mimeType = 'image/jpeg',
    quality = 0.92,
    maxEdge = 1920,
    fileName = 'cropped-image',
    trimMargins = false,
  } = options

  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is not available')

  let { width, height } = pixelCrop
  if (width <= 0 || height <= 0) {
    throw new Error('Invalid crop area')
  }

  const longest = Math.max(width, height)
  const scale = longest > maxEdge ? maxEdge / longest : 1
  const outW = Math.max(1, Math.round(width * scale))
  const outH = Math.max(1, Math.round(height * scale))

  canvas.width = outW
  canvas.height = outH

  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outW, outH)
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outW,
    outH,
  )

  const finalCanvas = trimMargins ? trimCanvas(canvas, mimeType === 'image/jpeg') : canvas

  const blob = await new Promise<Blob>((resolve, reject) => {
    finalCanvas.toBlob(
      (b) => {
        if (!b) reject(new Error('Failed to export cropped image'))
        else resolve(b)
      },
      mimeType,
      quality,
    )
  })

  const ext =
    mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg'
  const base = fileName.replace(/\.[^.]+$/, '') || 'cropped-image'
  return new File([blob], `${base}.${ext}`, { type: mimeType })
}

/** Prefer PNG when the source can carry transparency (logos). */
export function exportMimeForSource(sourceMime: string, preferPng: boolean): string {
  if (preferPng && (sourceMime === 'image/png' || sourceMime === 'image/webp' || sourceMime === 'image/gif')) {
    return 'image/png'
  }
  if (sourceMime === 'image/webp') return 'image/webp'
  return 'image/jpeg'
}
